/**
 * ViratAstro Ecosystem — comprehensive feature showcase.
 *
 * Rendered on HomePage between <Hero /> and <PoochoGurujiHero />.
 * Uses ONLY existing design tokens: gold/purple gradients, lucide icons,
 * glass-card / btn-gold / cosmic-bg-alt from index.css.
 *
 * Routes are wired to existing pages — NO new pages, NO new artwork.
 * "Ask Guruji" dispatches a window event consumed by FloatingActions.js
 * to open the existing AI chatbot drawer.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, Heart, Spade, Hash, CalendarDays, Sun,
  HelpCircle, MessageCircle, Home, Flame, BookOpen, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

/* ── Feature card definitions ────────────────────────────────────── */
const FEATURES = [
  {
    icon: Sparkles,
    title: 'कुंडली विश्लेषण',
    titleEn: 'Kundli Analysis',
    desc: 'Swiss Ephemeris एवं शास्त्रीय ज्योतिष पर आधारित विस्तृत वैदिक जन्म-कुंडली विश्लेषण।',
    cta: 'और जानें',
    to: '/kundli',
    tone: 'gold',
    testid: 'eco-card-kundli',
  },
  {
    icon: Heart,
    title: 'गुण मिलान',
    titleEn: 'Guna Milan',
    desc: 'विवाह की अनुकूलता हेतु 36 गुण मिलान, मांगलिक दोष एवं नाड़ी विश्लेषण।',
    cta: 'और जानें',
    to: '/gun-milan',
    tone: 'pink',
    testid: 'eco-card-gun-milan',
  },
  {
    icon: Spade,
    title: 'टैरो रीडिंग',
    titleEn: 'Tarot Reading',
    desc: 'करियर, रिश्तों, धन एवं जीवन-निर्णयों हेतु टैरो मार्गदर्शन।',
    cta: 'और जानें',
    to: '/daily-tarot',
    tone: 'purple',
    testid: 'eco-card-tarot',
  },
  {
    icon: Hash,
    title: 'अंकशास्त्र',
    titleEn: 'Numerology',
    desc: 'नाम एवं जन्म-तिथि पर आधारित विस्तृत अंकशास्त्रीय रिपोर्ट।',
    cta: 'और जानें',
    to: '/numerology',
    tone: 'indigo',
    testid: 'eco-card-numerology',
  },
  {
    icon: CalendarDays,
    title: 'आज का पंचांग',
    titleEn: 'Daily Panchang',
    desc: 'तिथि, नक्षत्र, योग, करण एवं शुभ मुहूर्त सहित दैनिक पंचांग।',
    cta: 'और जानें',
    to: '/daily-panchang',
    tone: 'amber',
    testid: 'eco-card-panchang',
  },
  {
    icon: Sun,
    title: 'दैनिक राशिफल',
    titleEn: 'Daily Horoscope',
    desc: '12 राशियों के लिए दैनिक भविष्यवाणी एवं शुभ कार्यों का मार्गदर्शन।',
    cta: 'और जानें',
    to: '/daily-rashifal',
    tone: 'sun',
    testid: 'eco-card-rashifal',
  },
  {
    icon: HelpCircle,
    title: 'पूछो गुरु जी',
    titleEn: 'Paid Q&A · Personal Astrology',
    desc: 'व्यक्तिगत ज्योतिषीय प्रश्नों के विस्तृत उत्तर — शास्त्रोक्त वैदिक सिद्धांतों के आधार पर।',
    examples: [
      'मेरा विवाह कब होगा?',
      'नौकरी में सफलता कब मिलेगी?',
      'विदेश योग है या नहीं?',
      'व्यवसाय में लाभ कब मिलेगा?',
    ],
    cta: '₹99 में प्रश्न पूछें',
    to: '/poocho-guruji',
    tone: 'premium',
    badge: '₹99 प्रति प्रश्न',
    featured: true,
    testid: 'eco-card-poocho-guruji',
  },
  {
    icon: MessageCircle,
    title: 'Ask Guruji',
    titleEn: 'Info Assistant (Free)',
    desc: 'अपॉइंटमेंट, परामर्श प्रक्रिया, सेवाएँ, फीस, वेबसाइट नेविगेशन — सब जानकारी के लिए।',
    note: 'ध्यान दें — व्यक्तिगत ज्योतिषीय भविष्यवाणी हेतु "पूछो गुरु जी" का उपयोग करें।',
    cta: 'Ask Guruji',
    action: 'open-chat',
    tone: 'cyan',
    testid: 'eco-card-ask-guruji',
  },
  {
    icon: Home,
    title: 'वास्तु परामर्श',
    titleEn: 'Vastu Consultation',
    desc: 'घर, ऑफिस एवं व्यावसायिक संपत्ति हेतु व्यावसायिक वास्तु परामर्श एवं विश्लेषण।',
    cta: 'और जानें',
    to: '/#vastu',
    tone: 'emerald',
    testid: 'eco-card-vastu',
  },
  {
    icon: Flame,
    title: 'नवग्रह उपाय',
    titleEn: 'Planetary Remedies',
    desc: 'पारंपरिक ज्योतिष पर आधारित ग्रह-दोष निवारण एवं आध्यात्मिक उपाय।',
    cta: 'और जानें',
    to: '/upay',
    tone: 'orange',
    testid: 'eco-card-upay',
  },
  {
    icon: BookOpen,
    title: 'ज्योतिष ब्लॉग',
    titleEn: 'Astrology Blog',
    desc: 'शैक्षणिक लेख, पर्व-त्योहार गाइड एवं आध्यात्मिक ज्ञान संसाधन।',
    cta: 'और जानें',
    to: '/blog',
    tone: 'rose',
    testid: 'eco-card-blog',
  },
];

/* ── Tone → gradient map (uses existing design tokens) ──────────── */
const TONE_GRADIENTS = {
  gold:    'from-gold/20 to-gold-dark/10',
  pink:    'from-pink-500/20 to-rose-500/10',
  purple:  'from-purple-light/20 to-purple/10',
  indigo:  'from-indigo-500/20 to-purple/10',
  amber:   'from-amber-500/20 to-orange-500/10',
  sun:     'from-yellow-500/20 to-amber-500/10',
  cyan:    'from-cyan-500/20 to-teal-500/10',
  emerald: 'from-emerald-500/20 to-green-600/10',
  orange:  'from-orange-500/20 to-red-500/10',
  rose:    'from-rose-500/20 to-pink-500/10',
  premium: 'from-gold/25 via-purple-light/15 to-gold/20',
};

/* ── Animation variants ─────────────────────────────────────────── */
const container = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const item = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

/* ── Single card ────────────────────────────────────────────────── */
function FeatureCard({ feature }) {
  const Icon = feature.icon;
  const gradient = TONE_GRADIENTS[feature.tone] || TONE_GRADIENTS.gold;
  const isFeatured = feature.featured;

  const cardContent = (
    <>
      {feature.badge && (
        <div className="absolute -top-2.5 -right-2.5 bg-gradient-to-r from-gold to-gold-dark text-purple-darker text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg shadow-gold/30 border border-gold-light/40 whitespace-nowrap">
          {feature.badge}
        </div>
      )}
      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 border border-white/5`}>
        <Icon className="w-6 h-6 text-gold" />
      </div>
      <h3 className="font-heading text-lg sm:text-xl text-text-main mb-1 leading-tight">
        {feature.title}
      </h3>
      <p className="text-gold/60 text-[11px] sm:text-xs mb-2.5 font-heading italic">
        {feature.titleEn}
      </p>
      <p className="text-text-muted text-xs sm:text-sm leading-relaxed">
        {feature.desc}
      </p>

      {feature.examples && (
        <ul className="mt-3 space-y-1">
          {feature.examples.map((ex, i) => (
            <li key={i} className="text-text-muted/85 text-[11px] sm:text-xs flex items-start gap-1.5">
              <span className="text-gold/70 mt-0.5">▸</span>
              <span>{ex}</span>
            </li>
          ))}
        </ul>
      )}

      {feature.note && (
        <p className="mt-3 text-[10px] sm:text-[11px] text-gold/70 italic leading-snug">
          {feature.note}
        </p>
      )}

      <div className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold ${
        isFeatured ? 'text-gold' : 'text-gold/85 group-hover:text-gold'
      } transition-colors`}>
        {feature.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
      </div>
    </>
  );

  const baseClass = `glass-card service-card p-5 sm:p-6 group cursor-pointer relative h-full flex flex-col ${
    isFeatured ? 'ring-1 ring-gold/40 shadow-lg shadow-gold/10' : ''
  }`;

  // "Ask Guruji" — opens the existing chatbot via global event
  if (feature.action === 'open-chat') {
    return (
      <motion.button
        type="button"
        variants={item}
        data-testid={feature.testid}
        onClick={() => window.dispatchEvent(new CustomEvent('viratastro:open-chat'))}
        className={`${baseClass} text-left`}
        aria-label={`${feature.title} — ${feature.cta}`}
      >
        {cardContent}
      </motion.button>
    );
  }

  // External-route cards
  const isAnchor = feature.to.startsWith('/#');
  if (isAnchor) {
    return (
      <motion.a
        variants={item}
        href={feature.to}
        data-testid={feature.testid}
        className={baseClass}
      >
        {cardContent}
      </motion.a>
    );
  }

  return (
    <motion.div variants={item} className="h-full">
      <Link to={feature.to} data-testid={feature.testid} className={baseClass} aria-label={`${feature.title} — ${feature.cta}`}>
        {cardContent}
      </Link>
    </motion.div>
  );
}

/* ── Section ─────────────────────────────────────────────────────── */
export default function Ecosystem() {
  return (
    <section
      id="ecosystem"
      className="section-padding relative overflow-hidden"
      data-testid="ecosystem-section"
    >
      <div className="absolute inset-0 cosmic-bg-alt" />
      <div className="absolute top-10 left-0 w-72 h-72 bg-gold/8 rounded-full blur-[120px]" />
      <div className="absolute bottom-10 right-0 w-72 h-72 bg-purple/8 rounded-full blur-[120px]" />

      <div className="container-custom relative z-10">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
          <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-gold font-semibold font-body mb-3 sm:mb-4">
            ViratAstro Ecosystem
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-medium text-text-main mb-4 sm:mb-6 tracking-tight leading-tight"
            data-testid="ecosystem-heading"
          >
            भारत का संपूर्ण <span className="text-gold-gradient">ज्योतिष एवं आध्यात्मिक</span> प्लेटफ़ॉर्म
          </h2>
          <p className="text-text-muted text-sm sm:text-base leading-relaxed">
            कुंडली, गुण मिलान, पंचांग, राशिफल, टैरो, अंकशास्त्र, वास्तु, नवग्रह उपाय और AI आधारित मार्गदर्शन — सब कुछ एक ही स्थान पर।
          </p>
          <div className="divine-divider mt-6 sm:mt-8 max-w-xs mx-auto" />
        </div>

        {/* Grid: 2 cols (mobile) · 3 cols (tablet) · 4 cols (desktop) */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
        >
          {FEATURES.map((f) => (
            <FeatureCard key={f.testid} feature={f} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
